/**
 * ScatterRenderer — outlier detection on two metrics with an optional third
 * mapped to symbol size.
 *
 * "Find me the offices that are weird on (obligation $, award count)"; bubble
 * size = avg award $. Outliers ("many tiny awards" / "few huge awards") jump
 * out without scrolling a sorted table.
 *
 * Phase C6 of issue #90 v3 plan.
 */
import { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import type { PerfRow, MetricKey, GroupByDimension } from './types'
import { METRIC_LABELS, formatMetric, getDimensionLabel, getDimensionKey } from './types'
import { useExecChartTheme } from './echarts-theme'

interface Props {
  data: PerfRow[]
  /** Each point represents one of these (typically 'awarding_office_code'). */
  groupBy: GroupByDimension
  /** [xMetric, yMetric] — first two are required for the scatter. */
  metrics: MetricKey[]
  /** Optional third metric → symbol size (bubble chart). */
  sizeMetric?: MetricKey
  onDrillDown?: (code: string, label: string) => void
}

export default function ScatterRenderer({ data, groupBy, metrics, sizeMetric, onDrillDown }: Props) {
  const theme = useExecChartTheme()

  // Hooks must run unconditionally; pull both metrics with safe fallbacks
  // and short-circuit on render rather than before the hook calls.
  const xMetric = metrics[0]
  const yMetric = metrics[1]

  const { points, sizeRange } = useMemo(() => {
    const pts: { value: [number, number, number?]; label: string; code: string }[] = []
    let sMin = Infinity
    let sMax = -Infinity
    for (const row of data) {
      const x = Number(row[xMetric])
      const y = Number(row[yMetric])
      if (Number.isNaN(x) || Number.isNaN(y)) continue
      const code = getDimensionKey(row, groupBy)
      const label = getDimensionLabel(row, groupBy)
      let s: number | undefined
      if (sizeMetric) {
        const sv = Number(row[sizeMetric])
        if (!Number.isNaN(sv)) {
          s = sv
          if (sv < sMin) sMin = sv
          if (sv > sMax) sMax = sv
        }
      }
      pts.push({ value: s !== undefined ? [x, y, s] : [x, y], label, code })
    }
    return { points: pts, sizeRange: { min: sMin, max: sMax } }
  }, [data, xMetric, yMetric, sizeMetric, groupBy])

  if (!xMetric || !yMetric) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-gray-400">
        Scatter requires two metrics (X, Y)
      </div>
    )
  }

  if (points.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-gray-400">
        No data
      </div>
    )
  }

  const sizeFn = sizeMetric && Number.isFinite(sizeRange.min) && sizeRange.max > sizeRange.min
    ? (v: number[]) => {
        const s = v[2] ?? 0
        const t = (s - sizeRange.min) / (sizeRange.max - sizeRange.min)
        return 6 + Math.sqrt(Math.max(0, t)) * 24
      }
    : 8

  const option: any = {
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        const [x, y, s] = params.value
        const lines = [
          `<b>${points[params.dataIndex]?.label ?? ''}</b>`,
          `${METRIC_LABELS[xMetric]}: <b>${formatMetric(xMetric, x)}</b>`,
          `${METRIC_LABELS[yMetric]}: <b>${formatMetric(yMetric, y)}</b>`,
        ]
        if (sizeMetric && s !== undefined) {
          lines.push(`${METRIC_LABELS[sizeMetric]}: <b>${formatMetric(sizeMetric, s)}</b>`)
        }
        return lines.join('<br/>')
      },
    },
    grid: { left: 60, right: 20, top: 20, bottom: 50 },
    xAxis: {
      type: 'value',
      name: METRIC_LABELS[xMetric],
      nameTextStyle: { fontSize: 10 },
      axisLabel: { fontSize: 10, formatter: (v: number) => formatMetric(xMetric, v) },
    },
    yAxis: {
      type: 'value',
      name: METRIC_LABELS[yMetric],
      nameTextStyle: { fontSize: 10 },
      axisLabel: { fontSize: 10, formatter: (v: number) => formatMetric(yMetric, v) },
    },
    series: [
      {
        type: 'scatter',
        data: points.map((p) => p.value),
        symbolSize: sizeFn,
      },
    ],
  }

  const onEvents = onDrillDown
    ? {
        click: (params: any) => {
          const p = points[params.dataIndex]
          if (p?.code) onDrillDown(p.code, p.label)
        },
      }
    : undefined

  return (
    <ReactECharts
      option={option}
      theme={theme}
      style={{ height: '100%', width: '100%' }}
      opts={{ renderer: 'canvas' }}
      onEvents={onEvents}
    />
  )
}
